import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { z } from 'zod';
import { DateTime } from 'luxon';
import { TransactionStatus, TransactionType } from '../../transaction/api/transaction-types';
import { useBudgetId } from '../../budget/budget-store';
import { useReconcileAccountApi } from '../api/use-reconcile-account';
import { toDollars, toPennies } from '../../utils/format';
// Reconcile Form Schema
export const reconcileFormSchema = z.object({
    balance: z.coerce.number().optional(),
    addAdjustment: z.boolean().default(false),
});
// Reconcile Form Hook
export const useReconcileForm = ({ account, currentBalance, onSuccess }) => {
    const budgetId = useBudgetId();
    // mutation
    const { mutate: reconcileAccount, isPending } = useReconcileAccountApi({ onSuccess });
    const defaultValues = useMemo(() => ({
        addAdjustment: false,
    }), [currentBalance]);
    const form = useForm({
        resolver: zodResolver(reconcileFormSchema),
        defaultValues,
    });
    const balance = form.watch('balance');
    const difference = balance === undefined || isNaN(balance || 0) ? 0 : toDollars(toPennies(balance || 0) - toPennies(currentBalance));
    useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues]);
    /**
     * Handles the form submission, building the API payload and
     * invoking the reconcile mutation.
     *
     * @param values - Sanitized reconcile form values.
     */
    const submitForm = useCallback((values) => {
        const now = DateTime.now();
        const { balance, addAdjustment } = values;
        const adjustmentAmount = (balance || 0) - currentBalance;
        const reconcileRequest = {
            id: account.id,
            budgetId,
            reconciledOn: now.toISO(),
            adjustment: addAdjustment && adjustmentAmount !== 0
                ? {
                    id: uuidv7(),
                    budgetId,
                    accountId: account.id,
                    type: TransactionType.ADJUSTMENT,
                    status: TransactionStatus.RECONCILED,
                    payee: 'Reconciliation Adjustment',
                    inflow: adjustmentAmount > 0 ? adjustmentAmount : 0,
                    outflow: adjustmentAmount < 0 ? Math.abs(adjustmentAmount) : 0,
                    dateEpoch: now.toUnixInteger(),
                }
                : undefined,
        };
        reconcileAccount(reconcileRequest);
    }, [account.id, budgetId, currentBalance, reconcileAccount]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isPending,
        difference,
    };
};
